'use client';

import ActionSheet from '@/components/ActionSheet';
import Dialog from '@/components/Dialog';
import { toast } from '@/components/Toast';
import { updateConsentsAfterAuth } from '@/hooks/useComplianceConsent';
import { useReporting } from '@/hooks/useReporting';
import { getStoreState } from '@/store';
import logger from '@/utils/client-logger';
import { COMPLIANCE_SCENE_CODES } from '@/utils/constants';
import { env } from '@/utils/env';
import { NewCloseOutlined, WapNavBackOutlined } from '@zeelool/icon';
import type {
  PDCardCalibrationEventCode,
  PDCardCalibrationMeasurementResult,
} from '@zeelool/pd-card-calibration';
import CircularProgress from '@mui/material/CircularProgress';
import Image from 'next/image';
import Link from 'next/link';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  resolveFaceMeasurementResult,
  resolvePdMeasurementResult,
  toRawFaceMeasurementResult,
} from '../result';
import {
  createMeasurementTimeoutController,
  type MeasurementTimeoutController,
} from '../measurement-timeout.client';
import {
  createMeasurementSdkSession,
  DEFAULT_MEASUREMENT_SDK_AUDIO_CONFIG,
  resolveMeasurementSdkFailureReason,
  type MeasurementPromptVisual,
  type MeasurementSdkAudioConfig,
  type MeasurementSdkConfig,
  type MeasurementSdkFailureReason,
  type MeasurementSdkSession,
} from '../sdk-adapter.client';
import { MEASUREMENT_PREPARATION_VIDEO_URL, MEASUREMENT_SDK_EVENT_CODE } from '../sdk-contract';
import { useMeasurementPersistence } from '../persistence.client';
import {
  FACE_WIDTH_MEASUREMENT_REQUEST_EVENT,
  PD_MEASUREMENT_REQUEST_EVENT,
} from '../request.client';
import type {
  FaceMeasurementRecord,
  FaceWidthMeasurementDialogMessages,
  FaceWidthMeasurementRequest,
  PdMeasurementRecord,
  PdMeasurementRequest,
} from '../types';
import {
  reportMeasurementButtonClick,
  reportMeasurementInteractionClick,
  reportMeasurementPageView,
} from '../tracking.client';
import { MEASUREMENT_TRACKING_STEP } from '../tracking';
import styles from './FaceWidthMeasurementDialogHost.module.css';
import MeasurementPromptIcon from './MeasurementPromptIcon';

type MeasurementRequest = FaceWidthMeasurementRequest | PdMeasurementRequest;

const CORRECT_EXAMPLE_IMAGE_BY_KIND: Record<MeasurementRequest['kind'], string> = {
  'face-width': '/zeelool-next/lens-measurement/face-width-correct-example.png',
  pd: '/zeelool-next/lens-measurement/pd-correct-example.png',
};

const INCORRECT_EXAMPLES_BY_KIND = {
  'face-width': [
    { image: 'distance.png', messageKey: 'tooClose' },
    { image: 'head-sideways.png', messageKey: 'sideways' },
    { image: 'head-up.png', messageKey: 'upOrDown' },
    { image: 'with-glasses.png', messageKey: 'glasses' },
  ],
  pd: [
    { image: 'distance.png', messageKey: 'tooClose' },
    { image: 'head-sideways.png', messageKey: 'sideways' },
    { image: 'head-up.png', messageKey: 'upOrDown' },
    { image: 'card-position.png', messageKey: 'tiltedCard' },
    { image: 'with-glasses.png', messageKey: 'glasses' },
  ],
} as const satisfies Record<
  MeasurementRequest['kind'],
  readonly {
    image: string;
    messageKey: 'tooClose' | 'sideways' | 'upOrDown' | 'tiltedCard' | 'glasses';
  }[]
>;

const FEEDBACK_ICON_BY_STATE = {
  dislike: {
    default: '/zeelool-next/lens-measurement/runtime/feedback-dislike-default.svg',
    selected: '/zeelool-next/lens-measurement/runtime/feedback-dislike-selected.svg',
  },
  like: {
    default: '/zeelool-next/lens-measurement/runtime/feedback-like-default.svg',
    selected: '/zeelool-next/lens-measurement/runtime/feedback-like-selected.svg',
  },
} as const;

type DialogState =
  | { step: 'closed' }
  | { step: 'method'; request: FaceWidthMeasurementRequest }
  | { step: 'pd-risk'; request: PdMeasurementRequest }
  | { step: 'pd-input'; request: FaceWidthMeasurementRequest }
  | { step: 'preparation'; request: MeasurementRequest; config: MeasurementSdkConfig }
  | { step: 'example'; request: MeasurementRequest; config: MeasurementSdkConfig }
  | { step: 'permission'; request: MeasurementRequest; config: MeasurementSdkConfig }
  | { step: 'camera'; request: MeasurementRequest; config: MeasurementSdkConfig }
  | {
      step: 'result';
      request: MeasurementRequest;
      config: MeasurementSdkConfig;
      record: FaceMeasurementRecord | PdMeasurementRecord;
    }
  | {
      step: 'failure';
      request: MeasurementRequest;
      config: MeasurementSdkConfig;
      reason: MeasurementSdkFailureReason | 'invalid-result' | 'timeout' | 'replace-card';
    };

const SINGLE_PD_OPTIONS = Array.from({ length: 49 }, (_, index) => {
  const value = 54 + index * 0.5;
  return {
    label: Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1),
    value: value.toFixed(2),
  };
});
const DUAL_PD_OPTIONS = Array.from({ length: 33 }, (_, index) => {
  const value = 25 + index * 0.5;
  return {
    label: Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1),
    value: value.toFixed(2),
  };
});
const PD_PICKER_Z_INDEX = 1400;
const MEASUREMENT_CONSENT_PAGE = '在线测量';

function getOpenStateConfig(state: DialogState) {
  return 'config' in state ? state.config : undefined;
}

function getOpenStateRequest(state: DialogState) {
  return 'request' in state ? state.request : undefined;
}

function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <span className={styles.speakerIconFrame} aria-hidden>
      <Image
        src={`/zeelool-next/lens-measurement/runtime/volume-${muted ? 'off' : 'on'}.svg`}
        alt=""
        width={24}
        height={24}
        className={styles.speakerIconGlyph}
      />
    </span>
  );
}

function PreparationSpeakerIcon({ muted }: { muted: boolean }) {
  return <SpeakerIcon muted={muted} />;
}

function CameraCloseIcon() {
  return (
    <span className={styles.closeIconFrame} aria-hidden>
      <Image
        src="/zeelool-next/lens-measurement/runtime/close.svg"
        alt=""
        width={15}
        height={15}
        className={styles.closeIconGlyph}
      />
    </span>
  );
}

function PermissionCameraIcon({ status }: { status: 'on' | 'off' }) {
  const dimensions =
    status === 'off' ? { width: 50, height: 44.6668 } : { width: 51.3339, height: 36.6667 };

  return (
    <span className={styles.permissionCameraIcon} aria-hidden>
      <Image
        src={`/zeelool-next/lens-measurement/runtime/camera-${status}.svg`}
        alt=""
        width={dimensions.width}
        height={dimensions.height}
      />
    </span>
  );
}

const PREPARATION_GUIDE_ICON = {
  card: {
    src: '/zeelool-next/lens-measurement/preparation-card.svg',
    width: 20.2,
    height: 14.8,
    className: styles.preparationCardGlyph,
  },
  lighting: {
    src: '/zeelool-next/lens-measurement/sun.svg',
    width: 23.0461,
    height: 23.0461,
    className: styles.preparationSunGlyph,
  },
  volume: {
    src: '/zeelool-next/lens-measurement/volume.svg',
    width: 17,
    height: 15,
    className: styles.preparationVolumeGlyph,
  },
} as const;

function PreparationGuideIcon({ kind }: { kind: keyof typeof PREPARATION_GUIDE_ICON }) {
  const icon = PREPARATION_GUIDE_ICON[kind];

  return (
    <span className={styles.preparationIconGlyphFrame} aria-hidden>
      <Image
        src={icon.src}
        alt=""
        width={icon.width}
        height={icon.height}
        className={icon.className}
      />
    </span>
  );
}

function CardFailureIcon() {
  return (
    <span className={styles.cardFailureIcon} aria-hidden>
      <Image
        src="/zeelool-next/lens-measurement/runtime/card-failure.svg"
        alt=""
        width={53.1997}
        height={38.7999}
        className={styles.cardFailureIconGlyph}
      />
    </span>
  );
}

function MeasurementFailureIcon() {
  return (
    <span className={styles.measurementFailureIcon} aria-hidden>
      <Image
        src="/zeelool-next/lens-measurement/runtime/measurement-failure.svg"
        alt=""
        width={50}
        height={50}
        className={styles.measurementFailureIconGlyph}
      />
    </span>
  );
}

function HeaderControls({
  backLabel,
  closeLabel,
  onBack,
  onClose,
}: {
  backLabel: string;
  closeLabel: string;
  onBack?: () => void;
  onClose: () => void;
}) {
  return (
    <header className={styles.headerControls}>
      {onBack ? (
        <button type="button" className={styles.iconButton} aria-label={backLabel} onClick={onBack}>
          <WapNavBackOutlined aria-hidden className={styles.controlIcon} />
        </button>
      ) : (
        <span />
      )}
      <button type="button" className={styles.iconButton} aria-label={closeLabel} onClick={onClose}>
        <NewCloseOutlined aria-hidden className={styles.controlIcon} />
      </button>
    </header>
  );
}

function PreparationControls({
  backLabel,
  closeLabel,
  muteLabel,
  unmuteLabel,
  isMuted,
  onBack,
  onClose,
  onToggleMuted,
}: {
  backLabel: string;
  closeLabel: string;
  muteLabel: string;
  unmuteLabel: string;
  isMuted: boolean;
  onBack: () => void;
  onClose: () => void;
  onToggleMuted: () => void;
}) {
  return (
    <>
      <div className={styles.preparationControls}>
        <button type="button" aria-label={backLabel} onClick={onBack}>
          <WapNavBackOutlined aria-hidden />
        </button>
        <button type="button" aria-label={closeLabel} onClick={onClose}>
          <NewCloseOutlined aria-hidden />
        </button>
      </div>
      <button
        type="button"
        className={styles.preparationSoundControl}
        aria-label={isMuted ? unmuteLabel : muteLabel}
        onClick={onToggleMuted}
      >
        <PreparationSpeakerIcon muted={isMuted} />
      </button>
    </>
  );
}

function playPreparationVideo(video: HTMLVideoElement) {
  void video.play().catch((error: unknown) => {
    logger.warn('[FaceMeasurement] Failed to play preparation video', { error });
  });
}

function PreparationVideo({ muted }: { muted: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [canPlay, setCanPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shouldPlay, setShouldPlay] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!canPlay || !shouldPlay || !video) return;

    playPreparationVideo(video);
  }, [canPlay, muted, shouldPlay]);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncReducedMotionPreference = () => {
      const nextShouldPlay = !reducedMotion.matches;
      setShouldPlay(nextShouldPlay);

      if (!nextShouldPlay) {
        setIsPlaying(false);
        videoRef.current?.pause();
      }
    };

    syncReducedMotionPreference();
    reducedMotion.addEventListener('change', syncReducedMotionPreference);
    return () => reducedMotion.removeEventListener('change', syncReducedMotionPreference);
  }, []);

  return (
    <>
      <Image
        src="/zeelool-next/lens-measurement/preparation-guide.png"
        alt=""
        fill
        sizes="100vw"
        priority
        className={`${styles.preparationPoster} ${isPlaying ? styles.preparationPosterHidden : ''}`}
      />
      {/* 视频仅作视觉演示，完整步骤说明由下方可访问文本提供。 */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={MEASUREMENT_PREPARATION_VIDEO_URL}
        poster="/zeelool-next/lens-measurement/preparation-guide.png"
        className={`${styles.preparationVideo} ${isPlaying ? styles.preparationVideoPlaying : ''}`}
        data-preparation-video
        data-ready={isPlaying}
        autoPlay
        loop
        muted={muted}
        playsInline
        preload="auto"
        aria-hidden="true"
        onCanPlay={() => setCanPlay(true)}
        onPlaying={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsPlaying(false)}
        onError={(event) => {
          setCanPlay(false);
          setIsPlaying(false);
          setShouldPlay(false);
          logger.warn('[FaceMeasurement] Failed to load preparation video', {
            error: event.currentTarget.error,
          });
        }}
      />
    </>
  );
}

function openLegalLink(event: MouseEvent<HTMLAnchorElement>, path: string) {
  event.preventDefault();
  window.open(path, '_blank');
}

function LegalNotice({
  messages,
  className,
}: {
  messages: FaceWidthMeasurementDialogMessages['method'];
  className?: string;
}) {
  return (
    <p className={`${styles.legalText} ${className || ''}`}>
      {messages.legalPrefix}{' '}
      <Link
        prefetch={false}
        href="/terms-conditions"
        onClick={(event) => openLegalLink(event, '/terms-conditions')}
      >
        {messages.terms}
      </Link>
      {messages.legalSeparator}{' '}
      <Link
        prefetch={false}
        href="/privacy-security"
        onClick={(event) => openLegalLink(event, '/privacy-security')}
      >
        {messages.privacy}
      </Link>{' '}
      {messages.legalConnector}{' '}
      <span className={styles.pendingLegalLink}>{messages.onlineAgreement}</span>.
    </p>
  );
}

function PrimaryButton({
  children,
  disabled,
  loading = false,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={styles.primaryButton}
      aria-busy={loading || undefined}
      disabled={disabled}
      onClick={onClick}
    >
      {loading ? <CircularProgress aria-hidden color="inherit" size={16} thickness={5} /> : null}
      {children}
    </button>
  );
}

function reportMeasurementPreparationConsent(
  request: MeasurementRequest,
  config: MeasurementSdkConfig,
) {
  if (!getStoreState().token) return;

  void updateConsentsAfterAuth({
    sceneCode: COMPLIANCE_SCENE_CODES.ONLINE_MEASUREMENT,
    page: MEASUREMENT_CONSENT_PAGE,
    status: 1,
    metadata: {
      measurement_kind: request.kind,
      measurement_source: request.source,
      measurement_mode: config.mode,
    },
  }).catch((error) => {
    logger.warn('[FaceMeasurement] Failed to update measurement compliance consent', { error });
  });
}

function OptionSelect({
  id,
  label,
  placeholder,
  centerValue,
  options,
  value,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  centerValue: string;
  options: Array<{ label: string; value: string }>;
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const displayValue = options.find((option) => option.value === value)?.label;

  return (
    <div className={styles.pdField}>
      <button
        id={id}
        type="button"
        className={styles.pdFieldButton}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-empty={value ? undefined : ''}
        onClick={() => setOpen(true)}
      >
        {value ? <span className={styles.pdFieldLabel}>{label}</span> : null}
        <span className={styles.pdFieldValue}>{displayValue || placeholder}</span>
      </button>
      <ActionSheet
        open={open}
        title={label}
        titleClassName="notranslate"
        options={options}
        defaultValue={centerValue}
        value={value}
        zIndex={PD_PICKER_Z_INDEX}
        onSave={(nextValue) => onChange(String(nextValue))}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}

export default function FaceWidthMeasurementDialogHost({
  messages,
  audioConfig = DEFAULT_MEASUREMENT_SDK_AUDIO_CONFIG,
  measurementTimeoutMs = 20_000,
  sessionFactory = createMeasurementSdkSession,
}: {
  messages: FaceWidthMeasurementDialogMessages;
  audioConfig?: MeasurementSdkAudioConfig;
  measurementTimeoutMs?: number;
  sessionFactory?: typeof createMeasurementSdkSession;
}) {
  const [dialogState, setDialogState] = useState<DialogState>({ step: 'closed' });
  const [isSdkReady, setIsSdkReady] = useState(false);
  const [isStartingCamera, setIsStartingCamera] = useState(false);
  const [promptCode, setPromptCode] = useState<PDCardCalibrationEventCode>();
  const [promptVisual, setPromptVisual] = useState<MeasurementPromptVisual>();
  const [measurementProgress, setMeasurementProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isDualPd, setIsDualPd] = useState(false);
  const [singlePd, setSinglePd] = useState('');
  const [rightPd, setRightPd] = useState('');
  const [leftPd, setLeftPd] = useState('');
  const [pdValidationVisible, setPdValidationVisible] = useState(false);
  const [feedback, setFeedback] = useState<'like' | 'dislike'>();
  const [isSavingResult, setIsSavingResult] = useState(false);
  const [sdkContainer, setSdkContainer] = useState<HTMLElement | null>(null);
  const measurementPersistence = useMeasurementPersistence();
  const { trackEvent } = useReporting();
  const sdkSessionRef = useRef<MeasurementSdkSession | null>(null);
  const timeoutControllerRef = useRef<MeasurementTimeoutController | null>(null);
  const activeMeasurementRef = useRef<{
    request: MeasurementRequest;
    config: MeasurementSdkConfig;
  } | null>(null);
  const invalidResultAttemptsRef = useRef(0);
  const isMutedRef = useRef(false);
  const isAwaitingRetryRef = useRef(false);
  const startRunIdRef = useRef(0);
  const promptCodeRef = useRef<PDCardCalibrationEventCode | undefined>(undefined);
  const measurementProgressRef = useRef(0);

  const disposeSdk = useCallback(() => {
    startRunIdRef.current += 1;
    timeoutControllerRef.current?.reset();
    sdkSessionRef.current?.dispose();
    sdkSessionRef.current = null;
    activeMeasurementRef.current = null;
    isAwaitingRetryRef.current = false;
    setIsSdkReady(false);
    setIsStartingCamera(false);
    setPromptCode(undefined);
    setPromptVisual(undefined);
    promptCodeRef.current = undefined;
    measurementProgressRef.current = 0;
    setMeasurementProgress(0);
  }, []);

  const closeDialog = useCallback(() => {
    disposeSdk();
    setDialogState({ step: 'closed' });
    setFeedback(undefined);
    setIsSavingResult(false);
  }, [disposeSdk]);

  useEffect(() => {
    const handleFaceWidthRequest = (event: Event) => {
      const request = (event as CustomEvent<FaceWidthMeasurementRequest>).detail;
      disposeSdk();
      invalidResultAttemptsRef.current = 0;
      setDialogState({ step: 'method', request });
    };
    const handlePdRequest = (event: Event) => {
      const request = (event as CustomEvent<PdMeasurementRequest>).detail;
      disposeSdk();
      invalidResultAttemptsRef.current = 0;
      setDialogState(
        request.prescriptionRisk === 'requires-confirmation'
          ? { step: 'pd-risk', request }
          : { step: 'preparation', request, config: { mode: 'card' } },
      );
    };

    window.addEventListener(FACE_WIDTH_MEASUREMENT_REQUEST_EVENT, handleFaceWidthRequest);
    window.addEventListener(PD_MEASUREMENT_REQUEST_EVENT, handlePdRequest);
    return () => {
      window.removeEventListener(FACE_WIDTH_MEASUREMENT_REQUEST_EVENT, handleFaceWidthRequest);
      window.removeEventListener(PD_MEASUREMENT_REQUEST_EVENT, handlePdRequest);
    };
  }, [disposeSdk]);

  useEffect(() => {
    window.addEventListener('pagehide', closeDialog);
    return () => window.removeEventListener('pagehide', closeDialog);
  }, [closeDialog]);

  useEffect(() => {
    const controller = createMeasurementTimeoutController({
      timeoutMs: measurementTimeoutMs,
      onTimeout: (step) => {
        const activeMeasurement = activeMeasurementRef.current;
        const session = sdkSessionRef.current;
        if (!activeMeasurement || !session) return;
        timeoutControllerRef.current?.reset();
        isAwaitingRetryRef.current = true;
        session.stopGuidance();
        setPromptCode(undefined);
        setPromptVisual(undefined);
        setDialogState({
          step: 'failure',
          request: activeMeasurement.request,
          config: activeMeasurement.config,
          reason: step === 'center-face' ? 'timeout' : 'replace-card',
        });
      },
    });
    const handleVisibilityChange = () => {
      controller.setVisible(document.visibilityState === 'visible');
    };

    timeoutControllerRef.current = controller;
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      controller.dispose();
      timeoutControllerRef.current = null;
    };
  }, [measurementTimeoutMs]);

  const sdkConfig = getOpenStateConfig(dialogState);
  const currentRequest = getOpenStateRequest(dialogState);
  const sdkMode = sdkConfig?.mode;
  const sdkPd = sdkConfig?.binocularPdMm;
  const entrySource = currentRequest?.source;
  const requestKind = currentRequest?.kind;
  const trackingStep =
    dialogState.step === 'preparation'
      ? MEASUREMENT_TRACKING_STEP.preparation
      : dialogState.step === 'example'
        ? MEASUREMENT_TRACKING_STEP.example
        : dialogState.step === 'permission'
          ? MEASUREMENT_TRACKING_STEP.permission
          : dialogState.step === 'camera'
            ? MEASUREMENT_TRACKING_STEP.camera
            : dialogState.step === 'result'
              ? MEASUREMENT_TRACKING_STEP.result
              : undefined;

  useEffect(() => {
    if (!requestKind || !entrySource || !sdkMode || !trackingStep) return;

    reportMeasurementPageView(trackEvent, {
      kind: requestKind,
      source: entrySource,
      mode: sdkMode,
      step: trackingStep,
    });
  }, [entrySource, requestKind, sdkMode, trackEvent, trackingStep]);

  useEffect(() => {
    if (!sdkMode || !entrySource || !sdkContainer || !currentRequest) return;

    let cancelled = false;
    const request = currentRequest;
    const config: MeasurementSdkConfig = {
      mode: sdkMode,
      ...(sdkPd === undefined ? {} : { binocularPdMm: sdkPd }),
    };

    disposeSdk();
    activeMeasurementRef.current = { request, config };
    invalidResultAttemptsRef.current = 0;

    const initialize = async () => {
      try {
        const session = await sessionFactory(
          sdkContainer,
          config,
          {
            onMessage: (code, visual) => {
              if (!cancelled && !isAwaitingRetryRef.current) {
                promptCodeRef.current = code;
                if (code !== MEASUREMENT_SDK_EVENT_CODE.Measuring) {
                  measurementProgressRef.current = 0;
                  setMeasurementProgress(0);
                }
                setPromptCode(code);
                setPromptVisual(visual);
                setIsStartingCamera(false);
                setDialogState((currentState) =>
                  currentState.step === 'permission'
                    ? { step: 'camera', request, config }
                    : currentState,
                );
              }
            },
            onProgress: (progress) => {
              if (
                cancelled ||
                isAwaitingRetryRef.current ||
                promptCodeRef.current !== MEASUREMENT_SDK_EVENT_CODE.Measuring ||
                !Number.isFinite(progress)
              ) {
                return;
              }
              const nextProgress = Math.round(Math.min(Math.max(progress, 0), 1) * 100);
              if (measurementProgressRef.current === nextProgress) return;
              measurementProgressRef.current = nextProgress;
              setMeasurementProgress(nextProgress);
            },
            onTimedStep: (step) => {
              if (!cancelled && !isAwaitingRetryRef.current) {
                timeoutControllerRef.current?.update(step);
              }
            },
            onResult: (sdkResult: PDCardCalibrationMeasurementResult) => {
              if (cancelled || isAwaitingRetryRef.current) return;
              timeoutControllerRef.current?.reset();
              const raw = toRawFaceMeasurementResult(sdkResult);
              const resolution =
                request.kind === 'pd'
                  ? resolvePdMeasurementResult({
                      raw,
                      entrySource: request.source,
                      sdkVersion: session.version,
                    })
                  : resolveFaceMeasurementResult({
                      raw,
                      entrySource: request.source,
                      sdkVersion: session.version,
                    });

              if (resolution.status === 'invalid') {
                if (invalidResultAttemptsRef.current === 0) {
                  invalidResultAttemptsRef.current = 1;
                  timeoutControllerRef.current?.reset();
                  setPromptCode(undefined);
                  setPromptVisual(undefined);
                  session.restart();
                  setDialogState({ step: 'camera', request, config });
                  return;
                }
                isAwaitingRetryRef.current = true;
                session.stopGuidance();
                setPromptCode(undefined);
                setPromptVisual(undefined);
                setDialogState({ step: 'failure', request, config, reason: 'invalid-result' });
                return;
              }

              session.stopGuidance();
              setFeedback(undefined);
              setDialogState({ step: 'result', request, config, record: resolution.record });
            },
          },
          audioConfig,
        );
        if (cancelled) {
          session.dispose();
          return;
        }
        sdkSessionRef.current = session;
        session.setMuted(isMutedRef.current);
        await session.init();
        if (!cancelled) setIsSdkReady(true);
      } catch (error) {
        if (cancelled) return;
        const reason = resolveMeasurementSdkFailureReason(error);
        if (reason === 'cancelled') return;
        disposeSdk();
        setDialogState({
          step: 'failure',
          request,
          config,
          reason,
        });
      }
    };

    void initialize();
    return () => {
      cancelled = true;
      disposeSdk();
    };
  }, [
    audioConfig,
    currentRequest,
    disposeSdk,
    entrySource,
    sdkContainer,
    sdkMode,
    sdkPd,
    sessionFactory,
  ]);

  const startCamera = async (request: MeasurementRequest, config: MeasurementSdkConfig) => {
    const session = sdkSessionRef.current;
    if (!session || !isSdkReady) return;
    const startRunId = startRunIdRef.current + 1;
    startRunIdRef.current = startRunId;
    const isCurrentStart = () =>
      startRunIdRef.current === startRunId && sdkSessionRef.current === session;

    setDialogState({ step: 'permission', request, config });
    isAwaitingRetryRef.current = false;
    timeoutControllerRef.current?.reset();
    setIsStartingCamera(true);
    setPromptCode(undefined);
    setPromptVisual(undefined);
    promptCodeRef.current = undefined;
    measurementProgressRef.current = 0;
    setMeasurementProgress(0);
    try {
      await session.start();
    } catch (error) {
      if (!isCurrentStart()) return;
      const reason = resolveMeasurementSdkFailureReason(error);
      if (reason === 'cancelled') {
        setDialogState({ step: 'preparation', request, config });
        return;
      }
      disposeSdk();
      setDialogState({
        step: 'failure',
        request,
        config,
        reason,
      });
    } finally {
      if (isCurrentStart()) setIsStartingCamera(false);
    }
  };

  const handlePdInputNext = (request: FaceWidthMeasurementRequest) => {
    const parsedSingle = Number(singlePd);
    const parsedRight = Number(rightPd);
    const parsedLeft = Number(leftPd);
    const pd = isDualPd ? parsedRight + parsedLeft : parsedSingle;
    if (!Number.isFinite(pd) || pd < 54 || pd > 78) {
      setPdValidationVisible(true);
      return;
    }
    setPdValidationVisible(false);
    setDialogState({
      step: 'preparation',
      request,
      config: { mode: 'pd-input', binocularPdMm: pd },
    });
  };

  const selectFaceWidthMeasurementMethod = (
    request: FaceWidthMeasurementRequest,
    mode: MeasurementSdkConfig['mode'],
  ) => {
    reportMeasurementPageView(trackEvent, {
      kind: request.kind,
      source: request.source,
      mode,
      step: MEASUREMENT_TRACKING_STEP.method,
    });
    setDialogState(
      mode === 'card'
        ? { step: 'preparation', request, config: { mode } }
        : { step: 'pd-input', request },
    );
  };

  const handleBack = () => {
    if (dialogState.step === 'pd-input' || dialogState.step === 'preparation') {
      if (dialogState.request.kind === 'face-width') {
        disposeSdk();
        setDialogState({ step: 'method', request: dialogState.request });
      } else {
        closeDialog();
      }
      return;
    }
    if (dialogState.step === 'example') {
      setDialogState({
        step: 'preparation',
        request: dialogState.request,
        config: dialogState.config,
      });
    }
  };

  const toggleMuted = () => {
    const nextMuted = !isMutedRef.current;
    isMutedRef.current = nextMuted;
    setIsMuted(nextMuted);
    sdkSessionRef.current?.setMuted(nextMuted);
  };

  const restartMeasurement = (request: MeasurementRequest, config: MeasurementSdkConfig) => {
    const session = sdkSessionRef.current;
    if (!session) return;
    isAwaitingRetryRef.current = false;
    invalidResultAttemptsRef.current = 0;
    timeoutControllerRef.current?.reset();
    setIsStartingCamera(false);
    setPromptCode(undefined);
    setPromptVisual(undefined);
    promptCodeRef.current = undefined;
    measurementProgressRef.current = 0;
    setMeasurementProgress(0);
    setFeedback(undefined);
    session.restart();
    setDialogState({ step: 'camera', request, config });
  };

  const retryFromFailure = () => {
    if (
      dialogState.step !== 'failure' ||
      !['invalid-result', 'timeout', 'replace-card'].includes(dialogState.reason)
    ) {
      return;
    }
    restartMeasurement(dialogState.request, dialogState.config);
  };

  const refreshPage = () => {
    disposeSdk();
    window.location.reload();
  };

  const isOpen = dialogState.step !== 'closed';
  const showCamera = dialogState.step === 'camera' || dialogState.step === 'result';
  const isFinalMeasurement = promptCode === MEASUREMENT_SDK_EVENT_CODE.Measuring;
  const prompt = promptCode ? messages.camera.prompts[promptCode] : messages.camera.starting;
  const resultPrimary =
    dialogState.step === 'result'
      ? 'onlinePdMm' in dialogState.record
        ? {
            label: messages.result.pdTitle,
            value: dialogState.record.onlinePdMm.toFixed(2),
          }
        : {
            label: messages.result.faceWidthTitle,
            value: dialogState.record.faceWidthMm.toFixed(1),
          }
      : null;
  const resultSubmitButtonName =
    dialogState.step === 'result'
      ? dialogState.request.kind === 'pd'
        ? messages.result.addPdToPrescription
        : messages.result.save
      : '';

  const remeasureFromResult = () => {
    if (dialogState.step !== 'result') return;

    reportMeasurementButtonClick(
      trackEvent,
      {
        kind: dialogState.request.kind,
        source: dialogState.request.source,
        mode: dialogState.config.mode,
        step: MEASUREMENT_TRACKING_STEP.result,
      },
      messages.result.remeasure,
    );
    if (feedback && !measurementPersistence.feedbackSubmitted) {
      void measurementPersistence.saveFeedback(feedback);
    }
    restartMeasurement(dialogState.request, dialogState.config);
  };

  const saveResult = async () => {
    if (dialogState.step !== 'result' || isSavingResult) return;
    const trackingContext = {
      kind: dialogState.request.kind,
      source: dialogState.request.source,
      mode: dialogState.config.mode,
      step: MEASUREMENT_TRACKING_STEP.result,
    } as const;
    reportMeasurementButtonClick(trackEvent, trackingContext, resultSubmitButtonName);
    if (!measurementPersistence.feedbackSubmitted) {
      reportMeasurementInteractionClick(trackEvent, trackingContext, feedback);
    }
    setIsSavingResult(true);
    const feedbackPromise =
      feedback && !measurementPersistence.feedbackSubmitted
        ? measurementPersistence.saveFeedback(feedback)
        : Promise.resolve({ saved: true });
    const [measurementResult] = await Promise.all([
      measurementPersistence.saveMeasurement(dialogState.record),
      feedbackPromise,
    ]);
    if (!measurementResult.saved) {
      toast.error(measurementResult.message || messages.failure.unavailable);
      setIsSavingResult(false);
      return;
    }
    closeDialog();
  };

  return (
    <Dialog
      open={isOpen}
      fullScreen
      hideHeader
      footer={null}
      closeOnClickOverlay={false}
      aria-label={messages.ariaLabel}
      onClose={closeDialog}
      contentProps={{ className: styles.dialogContent }}
      slotProps={{ paper: { className: styles.dialogPaper } }}
    >
      <div className={styles.shell}>
        <section
          ref={setSdkContainer}
          className={`${styles.sdkContainer} ${showCamera ? styles.sdkContainerVisible : ''}`}
          aria-label={messages.camera.label}
        />

        {dialogState.step === 'method' || dialogState.step === 'pd-input' ? (
          <section className={styles.contentScreen}>
            <HeaderControls
              backLabel={messages.backLabel}
              closeLabel={messages.closeLabel}
              onClose={closeDialog}
            />
            <main
              className={`${styles.screenBody} ${styles.methodBody} ${
                dialogState.step === 'method'
                  ? styles.methodBodyCollapsed
                  : isDualPd
                    ? styles.methodBodyDual
                    : ''
              }`}
            >
              <h1>{messages.method.title}</h1>
              <div className={styles.methodList}>
                <button
                  type="button"
                  aria-label={messages.method.cardTitle}
                  className={styles.methodCard}
                  onClick={() => selectFaceWidthMeasurementMethod(dialogState.request, 'card')}
                >
                  <span className={styles.methodCardCopy}>
                    <strong>{messages.method.cardTitle}</strong>
                    <small>{messages.method.cardDescription}</small>
                  </span>
                </button>

                {dialogState.step === 'method' ? (
                  <button
                    type="button"
                    aria-label={`${messages.method.pdTitle} ${messages.method.pdQualifier}`}
                    className={styles.methodCard}
                    onClick={() =>
                      selectFaceWidthMeasurementMethod(dialogState.request, 'pd-input')
                    }
                  >
                    <span className={styles.methodCardCopy}>
                      <strong>
                        {messages.method.pdTitle}{' '}
                        <span className={styles.methodQualifier}>
                          {messages.method.pdQualifier}
                        </span>
                      </strong>
                      <small>{messages.method.pdDescription}</small>
                    </span>
                  </button>
                ) : (
                  <section className={styles.methodCardExpanded}>
                    <div className={styles.methodCardCopy}>
                      <strong>
                        {messages.method.pdTitle}{' '}
                        <span className={styles.methodQualifier}>
                          {messages.method.pdQualifier}
                        </span>
                      </strong>
                      <small>{messages.method.pdDescription}</small>
                    </div>
                    <div className={styles.pdControls}>
                      <div className={styles.pdFields}>
                        {isDualPd ? (
                          <>
                            <OptionSelect
                              id="measurement-right-pd"
                              label={messages.pdInput.rightPdLabel}
                              placeholder={messages.pdInput.rightPdLabel}
                              centerValue="32.00"
                              options={DUAL_PD_OPTIONS}
                              value={rightPd}
                              onChange={setRightPd}
                            />
                            <OptionSelect
                              id="measurement-left-pd"
                              label={messages.pdInput.leftPdLabel}
                              placeholder={messages.pdInput.leftPdLabel}
                              centerValue="32.00"
                              options={DUAL_PD_OPTIONS}
                              value={leftPd}
                              onChange={setLeftPd}
                            />
                          </>
                        ) : (
                          <OptionSelect
                            id="measurement-single-pd"
                            label={messages.pdInput.pdLabel}
                            placeholder={messages.pdInput.pdPlaceholder}
                            centerValue="63.00"
                            options={SINGLE_PD_OPTIONS}
                            value={singlePd}
                            onChange={setSinglePd}
                          />
                        )}
                      </div>
                      <label className={styles.checkboxLabel} htmlFor="measurement-dual-pd">
                        <input
                          id="measurement-dual-pd"
                          type="checkbox"
                          aria-label={messages.pdInput.singleLabel}
                          checked={isDualPd}
                          onChange={(event) => {
                            setIsDualPd(event.target.checked);
                            if (event.target.checked) {
                              setRightPd((value) => value || '32.00');
                              setLeftPd((value) => value || '32.00');
                            }
                            setPdValidationVisible(false);
                          }}
                        />
                        {messages.pdInput.singleLabel}
                      </label>
                      {pdValidationVisible ? (
                        <p role="alert" className={styles.validationMessage}>
                          {messages.pdInput.validation}
                        </p>
                      ) : null}
                      <PrimaryButton onClick={() => handlePdInputNext(dialogState.request)}>
                        {messages.pdInput.next}
                      </PrimaryButton>
                    </div>
                  </section>
                )}
              </div>
              <LegalNotice messages={messages.method} />
            </main>
          </section>
        ) : null}

        {dialogState.step === 'preparation' ? (
          <section className={`${styles.contentScreen} ${styles.preparationScreen}`}>
            <div className={styles.preparationScrollArea} data-preparation-scroll-area>
              <div className={styles.preparationMedia}>
                <PreparationVideo muted={isMuted} />
                <PreparationControls
                  backLabel={messages.backLabel}
                  closeLabel={messages.closeLabel}
                  muteLabel={messages.muteLabel}
                  unmuteLabel={messages.unmuteLabel}
                  isMuted={isMuted}
                  onBack={handleBack}
                  onClose={closeDialog}
                  onToggleMuted={toggleMuted}
                />
              </div>
              <main className={`${styles.screenBody} ${styles.preparationBody}`}>
                <h1>{messages.preparation.title}</h1>
                <ul className={styles.preparationList}>
                  <li>
                    <span className={styles.preparationIcon} aria-hidden>
                      <PreparationGuideIcon kind="card" />
                    </span>
                    <span>{messages.preparation.card}</span>
                  </li>
                  <li>
                    <span className={styles.preparationIcon} aria-hidden>
                      <PreparationGuideIcon kind="lighting" />
                    </span>
                    <span>{messages.preparation.lighting}</span>
                  </li>
                  <li>
                    <span className={styles.preparationIcon} aria-hidden>
                      <PreparationGuideIcon kind="volume" />
                    </span>
                    <span>{messages.preparation.volume}</span>
                  </li>
                </ul>
                {!isSdkReady ? (
                  <p className="sr-only" aria-live="polite">
                    {messages.preparation.loading}
                  </p>
                ) : null}
                {dialogState.request.kind === 'pd' ? (
                  <p className={styles.preparationRiskNotice}>{messages.preparation.riskNotice}</p>
                ) : null}
              </main>
            </div>
            <footer
              className={`${styles.preparationFooter} ${
                dialogState.request.kind === 'pd' ? styles.preparationFooterWithLegal : ''
              }`}
            >
              <PrimaryButton
                disabled={!isSdkReady}
                loading={!isSdkReady}
                onClick={() => {
                  reportMeasurementPreparationConsent(dialogState.request, dialogState.config);
                  if (dialogState.config.mode === 'card') {
                    setDialogState({
                      step: 'example',
                      request: dialogState.request,
                      config: dialogState.config,
                    });
                  } else {
                    void startCamera(dialogState.request, dialogState.config);
                  }
                }}
              >
                {messages.preparation.next}
              </PrimaryButton>
              {dialogState.request.kind === 'pd' ? (
                <LegalNotice messages={messages.method} className={styles.preparationLegalText} />
              ) : null}
            </footer>
          </section>
        ) : null}

        {dialogState.step === 'pd-risk' ? (
          <section className={styles.prescriptionRiskOverlay}>
            <Image
              src="/zeelool-next/lens-measurement/pd-preparation-background.png"
              alt=""
              fill
              sizes="100vw"
              className={styles.prescriptionRiskBackground}
              aria-hidden
            />
            <div className={styles.prescriptionRiskScrim} aria-hidden />
            <div
              className={styles.prescriptionRiskDialog}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="prescription-risk-title"
            >
              <h1 id="prescription-risk-title">{messages.prescriptionRisk.title}</h1>
              <p>{messages.prescriptionRisk.description}</p>
              <p>
                {messages.prescriptionRisk.recommendation}{' '}
                <a href={env.NEXT_PUBLIC_PD_RULE_PDF} target="_blank" rel="noreferrer">
                  {messages.prescriptionRisk.pdRuler}
                </a>
                .
              </p>
              <div className={styles.prescriptionRiskActions}>
                <PrimaryButton
                  onClick={() =>
                    setDialogState({
                      step: 'preparation',
                      request: dialogState.request,
                      config: { mode: 'card' },
                    })
                  }
                >
                  {messages.prescriptionRisk.continue}
                </PrimaryButton>
                <button type="button" onClick={closeDialog}>
                  {messages.prescriptionRisk.exit}
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'example' ? (
          <section className={styles.contentScreen}>
            <HeaderControls
              backLabel={messages.backLabel}
              closeLabel={messages.closeLabel}
              onBack={handleBack}
              onClose={closeDialog}
            />
            <main className={`${styles.screenBody} ${styles.exampleBody}`}>
              <h1>{messages.example.title}</h1>
              <div className={styles.correctExample}>
                <div className={styles.correctImage}>
                  <Image
                    src={CORRECT_EXAMPLE_IMAGE_BY_KIND[dialogState.request.kind]}
                    alt=""
                    fill
                    sizes="164px"
                  />
                </div>
                <ul>
                  {[
                    messages.example.center,
                    messages.example.headCentered,
                    messages.example.removeGlasses,
                    messages.example.cardPosition,
                  ].map((instruction) => (
                    <li key={instruction}>
                      <span className={styles.exampleCheck} aria-hidden>
                        <Image
                          src="/zeelool-next/lens-measurement/check-circle.svg"
                          alt=""
                          width={14}
                          height={14}
                        />
                        <Image
                          src="/zeelool-next/lens-measurement/check-mark.svg"
                          alt=""
                          width={8}
                          height={6}
                        />
                      </span>
                      <span>{instruction}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.incorrectGrid}>
                {INCORRECT_EXAMPLES_BY_KIND[dialogState.request.kind].map(
                  ({ image, messageKey }) => (
                    <figure key={messageKey}>
                      <div className={styles.incorrectImage}>
                        <Image
                          src={`/zeelool-next/lens-measurement/${image}`}
                          alt=""
                          fill
                          sizes="100px"
                        />
                      </div>
                      <figcaption>{messages.example[messageKey]}</figcaption>
                    </figure>
                  ),
                )}
              </div>
            </main>
            <div className={styles.footerButton}>
              <PrimaryButton
                disabled={!isSdkReady}
                onClick={() => void startCamera(dialogState.request, dialogState.config)}
              >
                {messages.example.start}
              </PrimaryButton>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'permission' ? (
          <section className={styles.permissionOverlay} aria-live="polite">
            <Image
              src="/zeelool-next/lens-measurement/runtime/camera-permission-background.png"
              alt=""
              fill
              sizes="375px"
              className={styles.permissionBackground}
              aria-hidden
            />
            <button
              type="button"
              className={styles.permissionClose}
              aria-label={messages.closeLabel}
              onClick={closeDialog}
            >
              <CameraCloseIcon />
            </button>
            <div className={styles.permissionContent}>
              <PermissionCameraIcon status="on" />
              <p>{messages.camera.permission}</p>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'camera' ? (
          <section className={styles.cameraOverlay}>
            <div className={styles.cameraControls}>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={isMuted ? messages.unmuteLabel : messages.muteLabel}
                onClick={toggleMuted}
              >
                <SpeakerIcon muted={isMuted} />
              </button>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={messages.closeLabel}
                onClick={closeDialog}
              >
                <CameraCloseIcon />
              </button>
            </div>
            <div className={styles.cameraPromptGroup} aria-live="polite">
              {isFinalMeasurement ? (
                <div
                  className={styles.measurementProgress}
                  role="progressbar"
                  aria-label={messages.camera.progress.replace(
                    '{progress}',
                    String(measurementProgress),
                  )}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={measurementProgress}
                >
                  <span
                    className={styles.measurementProgressRing}
                    style={{ '--measurement-progress': `${measurementProgress}%` } as CSSProperties}
                    aria-hidden
                  />
                  <span className={styles.measurementProgressValue} aria-hidden>
                    {measurementProgress}%
                  </span>
                </div>
              ) : promptVisual ? (
                <MeasurementPromptIcon visual={promptVisual} />
              ) : null}
              <p>{isStartingCamera ? messages.camera.starting : prompt}</p>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'result' ? (
          <section className={styles.resultOverlay}>
            <div className={styles.resultSoundControl}>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={isMuted ? messages.unmuteLabel : messages.muteLabel}
                onClick={toggleMuted}
              >
                <SpeakerIcon muted={isMuted} />
              </button>
            </div>
            <div className={styles.resultSheet}>
              <button
                type="button"
                className={styles.resultCloseButton}
                aria-label={messages.closeLabel}
                onClick={closeDialog}
              >
                <CameraCloseIcon />
              </button>
              <div className={styles.resultPrimaryMetric}>
                <p>{resultPrimary?.label}</p>
                <strong>{resultPrimary?.value}</strong>
              </div>
              {!measurementPersistence.feedbackSubmitted ? (
                <>
                  <div className={styles.feedbackPrompt}>
                    <span aria-hidden />
                    <p>{messages.result.experience}</p>
                    <span aria-hidden />
                  </div>
                  <fieldset className={styles.feedback}>
                    <legend className="sr-only">{messages.result.feedbackLabel}</legend>
                    <button
                      type="button"
                      aria-label={messages.result.dislikeLabel}
                      aria-pressed={feedback === 'dislike'}
                      onClick={() => setFeedback('dislike')}
                    >
                      <Image
                        src={
                          FEEDBACK_ICON_BY_STATE.dislike[
                            feedback === 'dislike' ? 'selected' : 'default'
                          ]
                        }
                        alt=""
                        width={20}
                        height={20}
                        aria-hidden
                        className={styles.dislikeIcon}
                      />
                    </button>
                    <button
                      type="button"
                      aria-label={messages.result.likeLabel}
                      aria-pressed={feedback === 'like'}
                      onClick={() => setFeedback('like')}
                    >
                      <Image
                        src={
                          FEEDBACK_ICON_BY_STATE.like[feedback === 'like' ? 'selected' : 'default']
                        }
                        alt=""
                        width={20}
                        height={20}
                        aria-hidden
                      />
                    </button>
                  </fieldset>
                </>
              ) : null}
            </div>
            <div className={styles.resultActions}>
              <button type="button" disabled={isSavingResult} onClick={remeasureFromResult}>
                {messages.result.remeasure}
              </button>
              <PrimaryButton
                disabled={isSavingResult}
                loading={isSavingResult}
                onClick={saveResult}
              >
                {resultSubmitButtonName}
              </PrimaryButton>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'failure' && dialogState.reason === 'permission' ? (
          <section className={styles.permissionFailureOverlay}>
            <Image
              src="/zeelool-next/lens-measurement/runtime/camera-permission-background.png"
              alt=""
              fill
              sizes="375px"
              className={styles.permissionFailureBackground}
              aria-hidden
            />
            <button
              type="button"
              className={styles.permissionFailureClose}
              aria-label={messages.closeLabel}
              onClick={closeDialog}
            >
              <CameraCloseIcon />
            </button>
            <div className={styles.permissionFailureContent}>
              <PermissionCameraIcon status="off" />
              <p>{messages.failure.permission}</p>
              <PrimaryButton onClick={refreshPage}>{messages.failure.refresh}</PrimaryButton>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'failure' &&
        (dialogState.reason === 'unsupported' || dialogState.reason === 'integration') ? (
          <section className={styles.unsupportedFailureOverlay}>
            <Image
              src="/zeelool-next/lens-measurement/runtime/camera-permission-background.png"
              alt=""
              fill
              sizes="375px"
              className={styles.unsupportedFailureBackground}
              aria-hidden
            />
            <button
              type="button"
              className={styles.unsupportedFailureClose}
              aria-label={messages.closeLabel}
              onClick={closeDialog}
            >
              <CameraCloseIcon />
            </button>
            <div className={styles.unsupportedFailureContent} role="alert">
              <MeasurementFailureIcon />
              <p>
                {dialogState.reason === 'unsupported'
                  ? messages.failure.unsupported
                  : messages.failure.unavailable}
              </p>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'failure' && dialogState.reason === 'recoverable' ? (
          <section className={styles.recoverableFailureOverlay}>
            <Image
              src="/zeelool-next/lens-measurement/runtime/camera-permission-background.png"
              alt=""
              fill
              sizes="375px"
              className={styles.recoverableFailureBackground}
              aria-hidden
            />
            <button
              type="button"
              className={styles.recoverableFailureClose}
              aria-label={messages.closeLabel}
              onClick={closeDialog}
            >
              <CameraCloseIcon />
            </button>
            <div className={styles.recoverableFailureContent} role="alert">
              <MeasurementFailureIcon />
              <p>{messages.failure.recoverable}</p>
              <PrimaryButton onClick={refreshPage}>{messages.failure.refresh}</PrimaryButton>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'failure' && dialogState.reason === 'replace-card' ? (
          <section className={styles.cardFailureOverlay}>
            <div className={styles.cardFailureBackgroundFrame} aria-hidden>
              <Image
                src="/zeelool-next/lens-measurement/runtime/card-failure-background.png"
                alt=""
                fill
                sizes="663px"
                priority
              />
            </div>
            <span className={styles.cardFailureTint} aria-hidden />
            <div className={styles.cameraControls}>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={isMuted ? messages.unmuteLabel : messages.muteLabel}
                onClick={toggleMuted}
              >
                <SpeakerIcon muted={isMuted} />
              </button>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={messages.closeLabel}
                onClick={closeDialog}
              >
                <CameraCloseIcon />
              </button>
            </div>
            <div className={styles.cardFailureContent} role="alert">
              <CardFailureIcon />
              <p>{messages.failure.replaceCard}</p>
            </div>
            <div className={styles.cardFailureRetry}>
              <PrimaryButton onClick={retryFromFailure}>{messages.failure.retry}</PrimaryButton>
            </div>
          </section>
        ) : null}

        {dialogState.step === 'failure' &&
        (dialogState.reason === 'invalid-result' || dialogState.reason === 'timeout') ? (
          <section className={styles.measurementFailureOverlay}>
            <div className={styles.measurementFailureBackgroundFrame} aria-hidden>
              <Image
                src="/zeelool-next/lens-measurement/runtime/card-failure-background.png"
                alt=""
                fill
                sizes="663px"
                priority
              />
            </div>
            <span className={styles.measurementFailureTint} aria-hidden />
            <div className={styles.cameraControls}>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={isMuted ? messages.unmuteLabel : messages.muteLabel}
                onClick={toggleMuted}
              >
                <SpeakerIcon muted={isMuted} />
              </button>
              <button
                type="button"
                className={styles.cameraControlButton}
                aria-label={messages.closeLabel}
                onClick={closeDialog}
              >
                <CameraCloseIcon />
              </button>
            </div>
            <div className={styles.measurementFailureContent} role="alert">
              <MeasurementFailureIcon />
              <p>{messages.failure.retryMeasurement}</p>
            </div>
            <div className={styles.measurementFailureRetry}>
              <PrimaryButton onClick={retryFromFailure}>{messages.failure.retry}</PrimaryButton>
            </div>
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}
